import { isFetchError } from "fetcher"
import { isRoomJid, jidLocalPart, type Jid } from "../../xmpp/jid"
import { conversationFor } from "../selectors"
import type { ChatStore } from "../store"
import type { ReadyChatSession, ChatSession } from "../session"
import { loadHistory } from "./messages"
import type { ChatMessage, Conversation, LastEvent, Message } from "../../types"
import { messageHasId } from "../../xmpp/stanzas/message"
import * as chatApi from "../../api"
import { call } from "./call"

/** A tombstone is a deletion; a chat message carries its own flag. */
function isRetracted(message: Message): boolean {
  if (message.type === "tombstone") return true
  return message.type === "chat" ? message.retracted : false
}

/**
 * A live group event becomes the row's structured preview (FAME-703) rather
 * than dumping its raw sentence into `lastBody`. Needs the actor to render, so
 * an event with no `<author/>` yields nothing — the row keeps its prior preview.
 */
function lastEventOf(message: Message): LastEvent | undefined {
  if (message.type !== "event" || !message.author) return undefined

  const base = {
    by: message.author,
    at: new Date(message.timestamp).toISOString(),
  }
  switch (message.event) {
    case "renamed":
      return { ...base, type: "renamed", name: message.name }
    case "member_added":
    case "member_removed":
    case "member_left":
    case "promoted":
    case "demoted":
      return { ...base, type: message.event, member: message.member }
    default:
      return { ...base, type: message.event }
  }
}

/** An event carries a raw sentence in `body`; the row shows `lastEvent` instead. */
function previewBody(message: Message): string | undefined {
  return message.type === "event" ? undefined : message.body
}

/** True only for an image message; a newer message of any other kind clears it. */
function previewAttachment(message: Message): true | undefined {
  return message.type === "chat" && message.attachment ? true : undefined
}

/** The preview's sender, paired with `previewBody`: an event has no author line. */
function previewAuthor(message: Message): string | undefined {
  return message.type === "event" ? undefined : message.author
}

export function upsertConversation(
  store: ChatStore,
  message: Message,
  peer: Jid,
  unread?: boolean
) {
  const state = store.getState()
  const { conversations } = state
  const prev = conversationFor(state, peer)
  // Don't regress last-message info when an older message arrives (e.g. MAM).
  const isNewer = !prev || message.timestamp >= prev.lastTimestamp

  const next: Conversation = {
    peer,
    // The peer JID's domain is the discriminator the server derives the
    // /conversations `type` field from. Not the author: your own optimistic
    // echo to a room carries one too.
    type: prev?.type ?? (isRoomJid(peer) ? "group" : "direct"),
    // A message cannot carry the group's name, so a store-minted row starts
    // unnamed; the controller fetches the group to fill it in.
    name: prev?.name ?? null,
    archived: prev?.archived ?? false,
    // The row's preview follows the newest message, so a live one updates it
    // without waiting for a reseed. Body and event are mutually exclusive, so
    // the newest activity replaces whichever the row carried before.
    lastBody: isNewer ? previewBody(message) : prev?.lastBody,
    lastAuthor: isNewer ? previewAuthor(message) : prev?.lastAuthor,
    lastEvent: isNewer ? lastEventOf(message) : prev?.lastEvent,
    lastRetracted: isNewer
      ? isRetracted(message)
      : (prev?.lastRetracted ?? false),
    lastAttachment: isNewer ? previewAttachment(message) : prev?.lastAttachment,
    members: prev?.members,
    lastMessageId: isNewer ? message.id : (prev?.lastMessageId ?? message.id),
    lastTimestamp: isNewer
      ? message.timestamp
      : (prev?.lastTimestamp ?? message.timestamp),
    // `undefined` keeps the previous flag (outbound); a boolean sets it.
    unread: unread ?? prev?.unread ?? false,
    muted: prev?.muted,
  }

  store.setState({
    conversations: [next, ...conversations.filter((c) => c.peer !== peer)].sort(
      byRecency
    ),
  })
}

/**
 * Open a direct conversation before a word is said (FAME-779), so it survives a
 * reload if the composer is abandoned.
 *
 * Local first, request after: picking someone navigates straight to the thread,
 * so the sidebar must not wait on a round trip to show what is on screen. With
 * no token yet the local row still stands, and the first message is what tells
 * Costanza about the conversation.
 */
export function startDirectConversation(
  session: ChatSession,
  store: ChatStore,
  peer: Jid
) {
  ensureConversation(store, peer)

  const token = session.token
  if (!token) return

  void chatApi.createDirectConversation(token, peer).catch(session.recover)
}

/**
 * An empty direct row — no message, so no preview — for a peer the list does
 * not have yet. An existing conversation is left alone: opening it again is not
 * activity, so it keeps its place and its preview.
 */
function ensureConversation(store: ChatStore, peer: Jid) {
  const state = store.getState()
  if (conversationFor(state, peer)) return

  const row: Conversation = {
    peer,
    type: "direct",
    name: null,
    archived: false,
    lastRetracted: false,
    lastMessageId: "",
    lastTimestamp: Date.now(),
    unread: false,
  }

  store.setState({ conversations: [row, ...state.conversations] })
}

export function previewRetraction(
  store: ChatStore,
  peer: Jid,
  message: ChatMessage
) {
  const row = conversationFor(store.getState(), peer)
  if (!row || !messageHasId(message, row.lastMessageId)) return

  store.setState(({ conversations }) => ({
    conversations: conversations.map((c) =>
      c.peer === peer ? { ...c, lastRetracted: message.retracted } : c
    ),
  }))
}

/** An Edit of the last message is its new preview; one of a deleted message never shows. */
export function previewEdit(store: ChatStore, peer: Jid, message: ChatMessage) {
  const row = conversationFor(store.getState(), peer)
  if (!row || row.lastRetracted || !messageHasId(message, row.lastMessageId))
    return

  store.setState(({ conversations }) => ({
    conversations: conversations.map((c) =>
      c.peer === peer ? { ...c, lastBody: message.body } : c
    ),
  }))
}

export function renameConversation(store: ChatStore, peer: Jid, name: string) {
  store.setState(({ conversations }) => ({
    conversations: conversations.map((c) =>
      c.peer === peer ? { ...c, name } : c
    ),
  }))
}

/** The conversation on screen. Suppresses its own unread flag while open. */
export function setActivePeer(store: ChatStore, peer?: Jid) {
  store.setState({ activePeer: peer })
}

/**
 * No thread on screen. Clears the active peer so a closed conversation stops
 * suppressing its unread badge — otherwise the last-opened thread stays
 * "active" app-wide and its incoming messages arrive pre-read (FAME-712).
 */
export function closeConversation(store: ChatStore) {
  setActivePeer(store, undefined)
}

export function archiveConversation(
  store: ChatStore,
  peer: Jid,
  archived: boolean
) {
  store.setState(({ conversations }) => ({
    conversations: conversations.map((c) =>
      c.peer === peer ? { ...c, archived } : c
    ),
  }))
}

/**
 * Backfill messages received during a disconnect blip.
 */
export function backfillActiveConversation(
  session: ChatSession,
  store: ChatStore
) {
  const { activePeer } = store.getState()
  if (activePeer) void loadHistory(session, store, activePeer)
}

/**
 * Replace the conversation list with the server's.
 *
 * Takes the token rather than the session: boot seeds the list before the
 * socket exists, so a readiness check that wants a client would decline the
 * one call that matters most.
 */
export async function reseed(token: string, store: ChatStore) {
  const serverRows = await chatApi.getConversations(token)
  store.setState(({ conversations }) => ({
    conversations: mergeReseed(conversations, serverRows).sort(byRecency),
  }))
}

/**
 * The server list is authoritative, but a live message can land between the
 * fetch and this write — reseed runs on every reconnect, not just boot. A local
 * row newer than the server's snapshot keeps its own state (including a fresh
 * `unread` flag) rather than being reset to a read state the server had not yet
 * heard about. Rows the server no longer lists are dropped, as a
 * full replace did.
 *
 * A kept local row still takes the server's `members`: membership changes only
 * through a reseed, so the server's copy is never staler than ours. Without
 * this, adding or removing a member just after sending a message (which makes
 * the local row the newer one) would not show until the next reseed.
 * `muted` is the same: it changes only through the server.
 */
function mergeReseed(
  local: Conversation[],
  server: Conversation[]
): Conversation[] {
  const localByPeer = new Map(local.map((c) => [c.peer, c]))
  return server.map((row) => {
    const prev = localByPeer.get(row.peer)
    if (!prev || prev.lastTimestamp <= row.lastTimestamp) return row
    return { ...prev, members: row.members, muted: row.muted }
  })
}

const byRecency = (a: Conversation, b: Conversation) =>
  b.lastTimestamp - a.lastTimestamp

export function markConversationRead(
  session: ChatSession,
  store: ChatStore,
  peer: Jid
) {
  store.setState(({ conversations }) => ({
    conversations: conversations.map((c) =>
      c.peer === peer ? { ...c, unread: false } : c
    ),
  }))

  // HTTP, so a token is enough — no need to wait for the socket. Cleared
  // locally either way: the badge should go the moment the thread opens.
  const token = session.token
  if (!token) return

  void chatApi.markConversationRead(token, peer).catch(session.recover)
}

/**
 * Put a name on a row the store minted itself.
 *
 * A message cannot carry its group's name, so a room we are added to
 * mid-session arrives as `g-<uuid>` in the sidebar. Only the name is missing,
 * so only the name is fetched — a full reseed would replace every row, and
 * could resurrect an unread flag the user just cleared.
 */
export function nameUnseenRoom(
  session: ReadyChatSession,
  store: ChatStore,
  room: Jid
) {
  void chatApi
    .getGroup(session.token, jidLocalPart(room))
    .then(({ name }) => renameConversation(store, room, name))
    .catch(session.recover)
}

/**
 * The reader came back to the window: whatever landed in the open thread while
 * they were away is now seen. A no-op when nothing there is unread.
 */
export function markActiveConversationRead(
  session: ChatSession,
  store: ChatStore
) {
  const { activePeer, conversations } = store.getState()
  if (!activePeer) return
  if (!conversations.find((c) => c.peer === activePeer)?.unread) return
  markConversationRead(session, store, activePeer)
}

export async function openConversation(
  session: ChatSession,
  store: ChatStore,
  peer: Jid
) {
  // Which thread is on screen is local, so it switches whether or not we are
  // connected — a reconnecting socket must not leave the click doing nothing.
  setActivePeer(store, peer)

  // Each guards itself, and on different things: the read receipt needs a
  // token, the archive needs the socket. They become available at different
  // moments, so one gate for both would hold up whichever was ready first.
  markConversationRead(session, store, peer)
  // Our own Archive still holds a removed member's copy of the group, so
  // membership is checked before it is read.
  if (isRoomJid(peer) && !(await isMember(session, peer)))
    return dropRemovedGroup(store, peer)
  await loadHistory(session, store, peer)
}

/** Only the server refusing us counts: a failed check lets the thread load. */
async function isMember(session: ChatSession, room: Jid): Promise<boolean> {
  if (!session.token) return true

  try {
    await chatApi.getGroup(session.token, jidLocalPart(room))
    return true
  } catch (error) {
    return !(isFetchError(error) && [403, 404].includes(error.status ?? 0))
  }
}

/**
 * We are not a Member of this group: its row and thread go. Only a group on
 * screen steps back and tells the reader why, so no signal outlives the view
 * that consumes it.
 */
export function dropRemovedGroup(store: ChatStore, room: Jid) {
  store.setState(({ activePeer, conversations, messagesByPeer }) => {
    const { [room]: _dropped, ...kept } = messagesByPeer
    return {
      conversations: conversations.filter((c) => c.peer !== room),
      messagesByPeer: kept,
      ...(activePeer === room && {
        removedFrom: room,
        refusal: { kind: "not-member", peer: room } as const,
      }),
    }
  })
}

/** The view has stepped back from the group we were removed from. */
export function clearRemovedFrom(store: ChatStore) {
  store.setState({ removedFrom: undefined })
}

/**
 * Mute or unmute one conversation for this user. Local first, so the row and
 * the nav badge change at once; a refused request puts the row back and
 * rethrows for the caller to report.
 */
export function setConversationMuted(
  session: ChatSession,
  store: ChatStore,
  peer: Jid,
  muted: boolean
): Promise<void> {
  const setMuted = (value: boolean | undefined) =>
    store.setState(({ conversations }) => ({
      conversations: conversations.map((c) =>
        c.peer === peer ? { ...c, muted: value } : c
      ),
    }))

  const previous = conversationFor(store.getState(), peer)?.muted
  setMuted(muted)

  return call(session, (token) =>
    chatApi.setConversationMuted(token, peer, muted)
  ).catch((error: unknown) => {
    // A later toggle owns the row now; undoing this one would undo that.
    if (conversationFor(store.getState(), peer)?.muted === muted) {
      setMuted(previous)
    }
    throw error
  })
}
